/**
 * <schmancy-zoom-stage> — the file viewer's content stage, and one gesture
 * grammar over a photograph and a document alike.
 *
 * WHY IT EXISTS. `<embed type="application/pdf">` is not a viewer on iOS:
 * WebKit routes a subframe PDF to an ImageDocument and draws
 * `CGPDFDocumentGetPage(document, 1)` — one hard-coded page, flat, with no way
 * to reach page two. The embedded viewer's own zoom is unreachable from the
 * host page in every engine, and `transform: scale()` over a plugin magnifies a
 * bitmap instead of re-rasterising. An `<img class="object-contain">` is worse
 * still: it carries no gesture at all, so the same photograph zoomed or did not
 * depending on which door it was opened through. So the content is drawn here,
 * at the scale the reader chose, by one pointer map for both mimes.
 *
 * ZOOM HERE IS READING, NOT INSPECTION — every number below follows from that,
 * and each names where it comes from.
 *
 * THE GEOMETRY. One column drawn by `translate(tx,ty) scale(s)` about its own
 * top-left, laid out at the box scale 1 means FOR ITS OWN MIME:
 *
 *   application/pdf — the stage's own width. The column is then taller than the
 *     stage and ty travels thousands of px: SCROLL AND PAN ARE THE SAME NUMBER.
 *   image/*         — the largest box that fits BOTH axes, so one object is
 *     seen whole at rest. Every consumer opens this viewer on a landscape
 *     stage, where fit-width IS `object-cover` for anything taller than it —
 *     which would leave `object-contain`, the resting state a reader has today,
 *     below the floor and unreachable.
 *
 * Every bound falls out of one clamp either way.
 */
import { fromResizeObserver } from '@schmancy/directives/layout'
import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import type { PDFDocumentProxy, PDFWorker, TextLayer } from 'pdfjs-dist'
import {
	EMPTY,
	Observable,
	type OperatorFunction,
	Subject,
	animationFrameScheduler,
	animationFrames,
	auditTime,
	catchError,
	debounce,
	debounceTime,
	defer,
	distinctUntilChanged,
	filter,
	finalize,
	from,
	fromEvent,
	groupBy,
	map,
	merge,
	mergeMap,
	of,
	pairwise,
	share,
	switchMap,
	takeUntil,
	takeWhile,
	tap,
	throwError,
	timer,
} from 'rxjs'
import '../button/icon-button'
import '../progress/progress'
import '../surface/surface'
import '../typography/typography'
import type { FileViewerErrorDetail, FileViewerItem } from './file-viewer'

/** The floor. Nothing below it: content shrunk inside a sea of ground serves no
 *  reader a phone has. What scale 1 LAYS OUT is per mime; the number is not. */
const FIT = 1
/**
 * A document's ceiling. Apple's viewport default, Chromium's mobile default and
 * Chrome's own PDF zoom presets all stop at 5x, and three vendors agreeing is
 * the number. It is also what keeps a page under iOS Safari's canvas-area
 * refusal with room to spare at phone widths; MAX_CANVAS_AREA holds the wide
 * ones.
 */
const CEILING = 5
/** An image's floor ceiling. `MAX_SCALE` in `lightbox.ts` is 4 and caps a
 *  4000px photograph at a quarter of its own detail. The rule under both
 *  numbers is the same — zoom stops where information stops — so an image whose
 *  own pixels outrun this keeps going to where one image pixel meets one device
 *  pixel, and one that does not stops here with everything else. No canvas is
 *  involved in an image, so the raster budget that fixes CEILING for a page
 *  does not bind. */
const PHOTO_CEILING = CEILING
/** `DOUBLE_TAP_SCALE` in `lightbox.ts` — the photo convention, and the answer
 *  for content that carries no type to size against. */
const PHOTO_READING = 2
/** Where a double tap lands when a PAGE carries no measurable type (a scan, an
 *  image-only export): WebKit caps its own smart-magnify at 1.6, and a
 *  legibility scale is the target — never the ceiling. A page that does carry
 *  type is measured instead, below. */
const READING_FALLBACK = 1.6
/** This product's own body copy: `--text-body-md` = 14px. A document is
 *  readable when its own body type reaches the size the app around it reads at,
 *  which makes the reading scale a measurement rather than a preference. */
const BODY_MD_PX = 14
/**
 * One press of a zoom control, multiplicative. PDFKit's `zoomIn`/`zoomOut` is
 * the one zoom number Apple publishes, and it is published for exactly this
 * control: two presses double, six reach the ceiling from fit.
 */
const STEP = Math.SQRT2
/** UIScrollView's constant: past a bound the content follows at a diminishing
 *  rate, so an edge is felt rather than hit. */
const RESIST = 0.55
/** How far past each end a pinch travels before springing back — WebKit's
 *  `minElasticMagnification` / `maxElasticMagnification`. Chrome's PDF viewer
 *  hard-clamps instead; give at the limit is what a phone does everywhere else,
 *  and a phone is where the complaint came from. */
const ELASTIC_LOW = 0.75
const ELASTIC_HIGH = 1.333
/** How far past its own edge the content may be dragged, as a share of the
 *  host — Chromium's elastic overscroll. */
const OVERSCROLL = 0.1
/** Chromium's bounce: 15ms per px of overshoot, capped at SNAP_MS, which is
 *  also the overlay's own `SNAP_BACK_MS`. */
const BOUNCE_MS_PER_PX = 15
/** Per-frame velocity decay for a fling at 60fps — ~0.2s to halve. */
const FRICTION = 0.94
/** Below this the fling has stopped (px per ms). */
const FLING_FLOOR = 0.02
/**
 * One discrete zoom step's travel — and, because a press is released at the
 * end of its own transition rather than on idleness (see the release below),
 * what the reader waits for the sharp page.
 *
 * MEASURED on the running app, press to the sharper raster landing on the
 * canvas: 225ms on a one-page invoice and 229ms on a two-page document, which
 * is this constant plus the decode and the blit. The page is never blank
 * across it — the previous raster stands, magnified, until the new one is
 * whole — so what the reader sees is 200ms of softness rather than 200ms of
 * nothing, which is the trade the buffer below exists to make.
 */
const ZOOM_MS = 200
/** A double tap crosses the whole range at once, so it takes the duration the
 *  platform gives that exact gesture: Chromium's
 *  `kDoubleTapZoomAnimationDuration`. */
const DOUBLE_TAP_ZOOM_MS = 250
const SNAP_MS = 300
/** Tap and double-tap recognition, unchanged from the lightbox this library
 *  already ships — one hand, one grammar. */
const TAP_MOVE = 10
const TAP_MS = 250
const DOUBLE_TAP_MS = 300
const DOUBLE_TAP_PX = 40
/** How long the page readout stays after the last movement. */
const READOUT_DWELL_MS = 1200
/** A wheel or trackpad gesture has no pointerup, so idleness is its release. */
const WHEEL_SETTLE_MS = 150
/** Maps one wheel notch to one control step, so the two agree. */
const WHEEL_SENSITIVITY = (STEP - 1) / 100
/** Gap between pages — the only thing that says one sheet ended and another
 *  began. Read by the clamp above and written into the column's own style, so
 *  the two cannot drift. */
const PAGE_GAP_PX = 12
/**
 * A PDF paints ink and no ground, so the page carries its own white, pinned:
 * on a themed surface this document's dark ink lands on a dark page and
 * disappears the moment a reader is in dark mode. It rides the raster rather
 * than a CSS ground so the paper travels with the pixels.
 */
const PAPER = '#ffffff'
/**
 * One page's backing-store budget, in pixels — pdf.js's own `maxCanvasPixels`,
 * read from the copy this file renders with: 2**25 by default, and 5,242,880
 * once its `compatParams` sees a phone, because a device that crosses its real
 * limit gets a blank page rather than a slow one. The tier is taken from the
 * primary pointer rather than pdf.js's user-agent string, so a device is
 * classified by what it reports rather than by what it calls itself.
 *
 * A budget in pixels, not a multiple of the laid-out width: a multiple holds
 * the raster at a fixed size while the CSS scale keeps growing, so the page
 * goes soft exactly at the top of the range the reader zoomed for. MEASURED
 * against a fixed multiple of 4: at the 5x ceiling on a 361px stage at dpr 2,
 * the canvas carried 0.8 device px per CSS px — softer at 500% than at 100%.
 * Under this budget the page stays dpr-sharp to ~2.7x and degrades to 1.07:1
 * at the ceiling.
 */
const MAX_CANVAS_PX_TOUCH = 5_242_880
const MAX_CANVAS_PX_POINTER = 2 ** 25
/** iOS Safari refuses a canvas above 16,777,216 px² and paints NOTHING rather
 *  than degrading, so whatever budget applies is also held under this. */
const MAX_CANVAS_AREA = 16_777_216
/**
 * How far outside the stage a page is still worth a sharp raster: one screen
 * either side, which is what the hand-rolled on-screen arithmetic this replaces
 * admitted. It is the observer's own margin rather than a sum over `ty` and
 * `scale`, so the browser re-evaluates the band against the column's live
 * transform each frame and this file does not know the geometry twice.
 */
const BAND = '100% 0px'
/**
 * How many times a page that will not draw is asked again before the stage
 * stops asking it. A request arrives on every gesture release, every wheel
 * idle, every resize and every band change, so a page failing forever would
 * cost one full decode on the shared worker per gesture — queued ahead of the
 * pages that CAN draw — and a console line with it. Three attempts give a
 * transient (a font that arrived late, a decode that raced a reset) its second
 * and third chance; past that the page keeps the pixels it has.
 */
const DRAW_ATTEMPTS = 3

type Page = { index: number; ratio: number }
/** One page's pixels as the current geometry wants them. The two numbers are
 *  independent: `width` is the raster's own backing-store width and follows the
 *  reader's scale, `laidOutAt` is the scale-1 box the text layer is laid out
 *  against and follows the host box alone. `perPage` keys each leg on its own. */
type Raster = { doc: PDFDocumentProxy; page: number; width: number; laidOutAt: number }

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value))

/** Past a bound by `over` px against a viewport of `span`: how far the content
 *  actually moves. Asymptotes at `span`, and is 1:1 at zero so the edge has no
 *  seam. */
const resist = (over: number, span: number) =>
	span <= 0 ? 0 : (1 - 1 / ((Math.abs(over) * RESIST) / span + 1)) * span * Math.sign(over)

/** One parser worker serves every STAGE on the page; each document is its own
 *  loading task. `schmancy-pdf-thumbnail` keeps a singleton of its own off this
 *  same worker entry and does not export it, and an importer cannot assign to
 *  an imported binding, so a page carrying both a thumbnail and a stage runs
 *  two workers. Sharing them wants an exported accessor in `media-frame/`. */
let worker: PDFWorker | undefined

@schmancyElement('schmancy-zoom-stage')
export class SchmancyZoomStage extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				position: relative;
				overflow: hidden;
			}

			/* MEASURED with kevin_css against the running app: touch-action on :host
			   LOSES. schmancy's shared sheet carries a ":host, *" rule at layer base,
			   it is adopted into whatever shadow root this element is slotted into,
			   and its universal half reaches this element from that OUTER tree —
			   where a normal declaration outranks the shadow tree's own whatever the
			   layer. The same rule reaches this element's own descendants only from
			   INSIDE, where an unlayered declaration outranks layer base and wins.
			   So the browser hands over every touch here and nowhere above. */
			:host [data-stage] {
				touch-action: none;
				overscroll-behavior: contain;
			}

			/* pdf.js's text layer, scoped to this shadow root. Its spans are created
			   at runtime by TextLayer and carry no class a Tailwind scanner can see,
			   so their contract cannot live on a class= attribute: left/top are
			   percentages of the page, font-size is --font-height times the scale
			   var, and both are meaningless without these rules
			   (schmancy CASCADE_CRITICAL_GOES_IN_STATIC_CSS). Taken from
			   pdfjs-dist/web/pdf_viewer.css, reduced to what a read-only layer over
			   a canvas needs. */
			:host [data-text-layer] {
				position: absolute;
				inset: 0;
				overflow: clip;
				text-align: initial;
				line-height: 1;
				text-size-adjust: none;
				forced-color-adjust: none;
				transform-origin: 0 0;
				color-scheme: only light;
				--scale-round-x: 1px;
				--scale-round-y: 1px;
				--min-font-size: 1;
				--text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
				--min-font-size-inv: calc(1 / var(--min-font-size));
				/* A finger belongs to the gesture; a mouse can have the text. Hit
				   testing is the only thing this takes away, so find-in-page, copy and
				   the accessibility tree keep the layer on every device. */
				pointer-events: none;
			}
			@media (pointer: fine) {
				:host [data-text-layer] {
					pointer-events: auto;
				}
			}
			:host [data-text-layer] :is(span, br) {
				color: transparent;
				position: absolute;
				white-space: pre;
				cursor: text;
				transform-origin: 0% 0%;
				user-select: text;
			}
			:host [data-text-layer] > :not(.markedContent),
			:host [data-text-layer] .markedContent span:not(.markedContent) {
				z-index: 1;
				--font-height: 0;
				--scale-x: 1;
				--rotate: 0deg;
				font-size: calc(var(--text-scale-factor) * var(--font-height));
				transform: rotate(var(--rotate)) scaleX(var(--scale-x)) scale(var(--min-font-size-inv));
			}
			:host [data-text-layer] .markedContent {
				display: contents;
			}
			:host [data-text-layer] ::selection {
				background: color-mix(in srgb, AccentColor, transparent 50%);
				color: transparent;
			}
		`,
	]

	/** The viewer's own contract, unchanged — the stage adds no field to it. The
	 *  caller owns storage resolution and blob-URL lifetime, exactly as
	 *  `<schmancy-file-viewer>` already does. */
	@property({ attribute: false }) file!: FileViewerItem

	/** Empty until the content answers, which is what makes the page count and
	 *  the readout facts a read established rather than assumed. */
	@state() private pages: Page[] = []
	@state() private phase: 'reading' | 'ready' | 'failed' = 'reading'

	private stageRef = createRef<HTMLElement>()
	private columnRef = createRef<HTMLElement>()
	private readoutRef = createRef<HTMLElement>()
	private pageRef = createRef<HTMLElement>()
	private liveRef = createRef<HTMLElement>()
	private zoomOutRef = createRef<HTMLElement & { disabled: boolean }>()
	private zoomInRef = createRef<HTMLElement & { disabled: boolean }>()

	/** Live geometry. Plain fields, never `@state`: a pinch writes these every
	 *  few ms, and routing that through Lit would re-render the column each frame
	 *  for a value one element reads. Settled facts go through `@state`; frames
	 *  go straight to `style.transform`. */
	private scale = FIT
	private tx = 0
	private ty = 0
	/** The column's laid-out box at scale 1 — see THE GEOMETRY above for what
	 *  each mime's box is, and why they differ. */
	private cw = 0
	private ch = 0
	/** Which pages the observer currently reports inside the band. */
	private onScreen = new Set<number>()
	private textLayers = new Map<HTMLElement, TextLayer>()
	/** Ceiling and double-tap target. Per mime, and re-derived in `measure()`
	 *  against whatever box the stage currently has rather than frozen at the one
	 *  the content was read on. */
	private ceiling = CEILING
	private reading = READING_FALLBACK
	/** Set by the image read: the file's own pixel width, and 0 for a document.
	 *  It is what pays for an image's ceiling above the shared one. */
	private naturalWidth = 0
	/** Set by the document read: its own body type and page box, in PDF points,
	 *  so the reading scale is re-derived against the current width. */
	private bodyPt = 0
	private pageWidthPt = 0
	/** 1-based, the page whose box covers the middle of the host. */
	private page = 1

	private pointers = new Map<number, { x: number; y: number }>()
	private pinch: { distance: number; scale: number; ax: number; ay: number } | undefined
	private pressed: { x: number; y: number; tx: number; ty: number; at: number } | undefined
	private samples: { t: number; x: number; y: number }[] = []
	private lastTap: { at: number; x: number; y: number } | undefined
	/** The scale a macOS trackpad pinch started from, since `GestureEvent.scale`
	 *  is cumulative from the gesture's start rather than a per-event delta —
	 *  multiplying it each callback grows the content exponentially. */
	private gestureScale = FIT
	/** The scale the live region last said out loud. Without it a pan that
	 *  crossed into page 2 announced the zoom instead: settle() runs at the end
	 *  of every gesture, pans included, and overwrote the page just announced. */
	private announcedScale = FIT
	private doc: PDFDocumentProxy | undefined
	private pdfjs: typeof import('pdfjs-dist') | undefined

	private settle$ = new Subject<void>()
	private fling$ = new Subject<{ vx: number; vy: number }>()
	/** Carries the travel the paint that emitted it set going, so a release can
	 *  wait the motion out rather than firing into the middle of it. */
	private moved$ = new Subject<number>()
	private requested$ = new Subject<void>()
	/** The geometry settled: every page's canvas may now want different pixels. */
	private rasterRequest$ = new Subject<void>()
	/** The page boxes in the DOM changed, so the band observer needs re-aiming. */
	private paged$ = new Subject<void>()

	override connectedCallback(): void {
		super.connectedCallback()
		merge(
			// A fling is one session: frames until friction or an edge ends it, and a
			// new press cancels it outright.
			this.fling$.pipe(
				switchMap(start => {
					let vx = start.vx
					let vy = start.vy
					return animationFrames().pipe(
						pairwise(),
						map(([before, now]) => now.timestamp - before.timestamp),
						tap(dt => {
							const decay = FRICTION ** (dt / 16.67)
							vx *= decay
							vy *= decay
							this.tx += vx * dt
							this.ty += vy * dt
							const before = { x: this.tx, y: this.ty }
							this.clampPan()
							if (this.tx !== before.x) vx = 0
							if (this.ty !== before.y) vy = 0
							this.paint()
						}),
						takeWhile(() => Math.hypot(vx, vy) > FLING_FLOOR),
						takeUntil(merge(this.settle$, fromEvent(this, 'pointerdown'))),
					)
				}),
			),
			// The read. switchMap, so a file changed while a document is still
			// parsing abandons that read rather than letting it land over the new
			// one's pages.
			this.requested$.pipe(
				tap(() => {
					for (const textLayer of this.textLayers.values()) textLayer.cancel()
					this.textLayers.clear()
					this.phase = 'reading'
					this.pages = []
					// Released, not merely dropped. The reader this engine came from
					// mounted for one file and never had its source reassigned, so
					// letting the proxy fall out of scope cost nothing. Here ONE
					// element serves a whole set — the viewer binds `.file=` and never
					// re-keys — so every page-turn through a multi-PDF set strands
					// another document's pages and fonts on the worker.
					//
					// The release itself is the DOCUMENT SESSION's teardown below, not a
					// line here: `cleanup()` refuses while a page is still rendering
					// (pdfjs 6.4 `pdf.mjs:16055`) and `startCleanup` throws on the
					// first page that refuses (`:16950`), so asked for here — with
					// this document's render tasks still in flight — it rejected, and
					// every page after the throwing one plus the font, filter and
					// text-layer caches were skipped in silence. Unsubscribing the
					// session cancels those tasks first, which is the only order in
					// which the release can succeed.
					this.doc = undefined
					this.scale = FIT
					this.tx = 0
					this.ty = 0
					this.ceiling = CEILING
					this.reading = READING_FALLBACK
					this.naturalWidth = 0
					this.bodyPt = 0
					this.pageWidthPt = 0
					this.say(componentText('zoomStage.loading', { name: this.file?.name || componentText('zoomStage.file') }))
				}),
				// The parser loads with the branch that needs it and never with the
				// one that does not: `./file-viewer` is its own dist entry, so an
				// unconditional import bills ~496 KB gzip on every image this viewer
				// ever opens.
				switchMap(() => {
					const file = this.file
					return defer(() => (file.mime === 'application/pdf' ? this.readDocument() : this.readImage())).pipe(
						catchError(() => {
							if (this.file.previewUrl !== file.previewUrl || this.file.mime !== file.mime || !this.isConnected)
								return EMPTY
							this.phase = 'failed'
							this.say(componentText('zoomStage.failed'))
							this.dispatchEvent(
								new CustomEvent<FileViewerErrorDetail>('schmancy-file-viewer-error', {
									detail: {
										file: { id: this.file.id, name: this.file.name, mime: this.file.mime },
										error: { code: 'decode-failed', message: componentText('zoomStage.decodeFailed') },
									},
									bubbles: true,
									composed: true,
								}),
							)
							return EMPTY
						}),
					)
				}),
				tap(() => {
					this.phase = 'ready'
					const label = this.file?.name || componentText('zoomStage.file')
					this.say(
						this.pages.length > 1 ? componentText('zoomStage.pages', { name: label, count: this.pages.length }) : label,
					)
				}),
				switchMap(() => from(this.updateComplete)),
				tap(() => {
					this.onScreen.clear()
					this.paged$.next()
					this.measure()
				}),
				catchError((error, retry) => {
					console.error('schmancy-zoom-stage: could not read the file', error)
					this.phase = 'failed'
					this.say(componentText('zoomStage.failed'))
					return retry
				}),
			),
			// Trackpad pinch arrives as a ctrl-wheel; a plain wheel is the content
			// moving under the cursor. Neither has a release of its own.
			fromEvent<WheelEvent>(this, 'wheel', { passive: false }).pipe(
				tap(event => {
					event.preventDefault()
					const column = this.columnRef.value
					if (column) column.style.transition = 'none'
					if (event.ctrlKey) {
						this.zoomTo(this.scale * (1 - event.deltaY * WHEEL_SENSITIVITY), event.clientX, event.clientY, 0, true)
						return
					}
					this.tx -= event.deltaX
					this.ty -= event.deltaY
					this.clampPan()
					this.paint()
				}),
			),
			// Safari on macOS is the one engine that synthesises no ctrl-wheel for a
			// trackpad pinch: it fires gesturestart/change/end and nothing else, so
			// without this a reviewer on a Mac has no trackpad zoom at all.
			fromEvent<Event & { scale: number; clientX: number; clientY: number }>(this, 'gesturestart').pipe(
				tap(event => {
					event.preventDefault()
					this.gestureScale = this.scale
				}),
			),
			fromEvent<Event & { scale: number; clientX: number; clientY: number }>(this, 'gesturechange').pipe(
				tap(event => {
					event.preventDefault()
					this.zoomTo(this.gestureScale * event.scale, event.clientX, event.clientY, 0, true)
				}),
			),
			fromEvent(this, 'gestureend').pipe(tap(() => this.settle())),
			this.moved$.pipe(
				// A MOTION THE STAGE SET GOING IS ITS OWN RELEASE; a motion with no
				// end of its own releases on idleness. A press — a zoom button, a
				// `+`, an arrow, the spring-back a settle paints — travels for exactly
				// as long as the transition it started, and the frame that lands is
				// the moment the geometry is final. A wheel notch, a finger and a
				// trackpad pinch carry no end at all, so stillness has to stand in for
				// one. Repeats coalesce either way: each paint restarts this, so
				// holding `+` asks for one raster after the last press rather than one
				// per press.
				debounce(travel => timer(travel > 0 ? travel : WHEEL_SETTLE_MS)),
				// A finger resting mid-drag is not a release: only a gesture with no
				// pointer left down settles on idleness.
				filter(() => this.pointers.size === 0),
				tap(() => this.settle()),
			),
			// The readout follows movement and leaves on its own.
			this.moved$.pipe(tap(() => this.showReadout(this.pages.length > 1))),
			this.moved$.pipe(
				debounceTime(READOUT_DWELL_MS),
				tap(() => this.showReadout(false)),
			),
			fromEvent<KeyboardEvent>(this, 'keydown').pipe(
				tap(event => {
					const step = this.clientHeight * 0.85
					const nudge = 64
					const move = (dx: number, dy: number) => {
						event.preventDefault()
						this.tx += dx
						this.ty += dy
						this.clampPan()
						this.paint(ZOOM_MS)
						this.settle$.next()
					}
					switch (event.key) {
						case '+':
						case '=':
							event.preventDefault()
							return this.step(this.scale * STEP, this.centre(), ZOOM_MS)
						case '-':
						case '_':
							event.preventDefault()
							return this.step(this.scale / STEP, this.centre(), ZOOM_MS)
						case '0':
							event.preventDefault()
							return this.step(FIT, this.centre(), ZOOM_MS)
						case 'ArrowDown':
							return move(0, -nudge)
						case 'ArrowUp':
							return move(0, nudge)
						case 'ArrowLeft':
							// Only while there is something to pan to: at fit the horizontal
							// axis belongs to the file set, whose own paging keeps it. The
							// viewer reads `defaultPrevented` to learn which of the two
							// happened, so leaving this unprevented is the handover.
							if (this.cw * this.scale <= this.clientWidth) return
							return move(nudge, 0)
						case 'ArrowRight':
							if (this.cw * this.scale <= this.clientWidth) return
							return move(-nudge, 0)
						case 'PageDown':
							return move(0, -step)
						case 'PageUp':
							return move(0, step)
						case 'Home':
							return move(0, Number.POSITIVE_INFINITY)
						case 'End':
							return move(0, Number.NEGATIVE_INFINITY)
						default:
							return
					}
				}),
			),
			// THE DOCUMENT SESSION. Everything that touches a parsed document — the
			// band observer and both raster legs — lives inside one session per
			// document, and the release of that document is the session's own
			// teardown. `switchMap` is what orders them: the next document cannot
			// start until this one has unsubscribed, and unsubscribing is what
			// cancels every render task still in flight. Asked for any earlier,
			// `cleanup()` refuses and throws (see the read's reset above).
			//
			// It also bounds what the groups below hold. A `groupBy` group never
			// completes, so each leg's `distinctUntilChanged` keeps the last request
			// it saw — and with it that request's `PDFDocumentProxy` — for as long
			// as the group lives. Scoped here, that is the document's own lifetime
			// rather than the element's.
			//
			// `merge` with a `defer`, because the read pushes `paged$` only when the
			// FILE changes: reconnecting after a reparent re-subscribes this chain
			// without one, and a session that never starts leaves the band observer
			// gone while `onScreen` — a plain field — survives frozen, so the stage
			// would raster the stale band once and never update again.
			merge(
				this.paged$,
				defer(() => of(undefined)),
			).pipe(
				switchMap(() => {
					const doc = this.doc
					// One request set per ask, read once from live geometry and shared
					// by both legs, so the two cannot be handed different facts.
					const requests$ = this.rasterRequest$.pipe(
						mergeMap(() => from(this.rasters())),
						share(),
					)
					return merge(
						// Which pages are worth drawing.
						new Observable<void>(subscriber => {
							const observer = new IntersectionObserver(
								entries => {
									for (const entry of entries) {
										const index = Number((entry.target as HTMLElement).dataset.pageBox)
										if (entry.isIntersecting) this.onScreen.add(index)
										else this.onScreen.delete(index)
									}
									subscriber.next()
								},
								{ root: this, rootMargin: BAND },
							)
							for (const box of this.renderRoot.querySelectorAll('[data-page-box]')) observer.observe(box)
							return () => observer.disconnect()
						}).pipe(
							// Out of the observer's own callback before anything is written,
							// which is the discipline `fill()` and the virtualizer both
							// record paying for: work done inside delivery re-enters the
							// observer. It also coalesces a burst of entries — every page of
							// a short document crossing the band at once — into one request.
							auditTime(0, animationFrameScheduler),
							tap(() => this.rasterRequest$.next()),
						),
						// THE RASTER, flattened PER PAGE rather than across pages: a new
						// width for page 3 supersedes page 3's own draw and must leave
						// page 2's alone, which one `switchMap` over the visible set would
						// not. The inner `switchMap` is what a generation guard over a
						// WeakMap and a hand-rolled `RenderTask.cancel()` were doing by
						// hand; unsubscribing now cancels the draw, through the one
						// `finalize` that owns the task and its buffer.
						//
						// Concurrency is bounded by the two constraints this file already
						// names rather than by a number on the flatten. The band admits
						// only the pages within one screen of the stage — three at fit,
						// fewer zoomed in — and the module-singleton worker parses one page
						// at a time whatever is asked of it, so a wider fan-out buys no
						// earlier pixels and only holds more buffers at once. A
						// `mergeMap(…, N)` over the groups could not express that bound in
						// any case: a `groupBy` group never completes, so every page past
						// the Nth would wait on a slot that never frees.
						requests$.pipe(
							this.perPage(
								(before, now) => before.doc === now.doc && before.width === now.width,
								request => this.drawPage(request),
							),
						),
						// THE TEXT LAYER, on its own key beside the raster rather than
						// inside it. It is laid out against the scale-1 box, so it moves
						// when the HOST box moves and stands still through every zoom — the
						// opposite of the raster, whose width the host box can leave
						// untouched. `perPage` carries the measurement of what sharing one
						// key cost.
						requests$.pipe(
							this.perPage(
								(before, now) => before.doc === now.doc && before.laidOutAt === now.laidOutAt,
								request => this.layOutText(request),
							),
						),
					).pipe(
						finalize(() => {
							// `cleanup()`, not the loading task's `destroy()`: that one ends
							// with an unconditional `this._worker?.destroy()`
							// (pdfjs 6.4 `pdf.mjs:15597`) over the worker it was handed,
							// which here is the module singleton every stage on the page
							// shares. A fuller per-document release wants the worker owned
							// per document, or the shared accessor `media-frame/` does not
							// yet export. The rejection is reported rather than swallowed:
							// a refused release means pages and fonts are still on that
							// shared worker, which is the whole reason this call exists.
							void doc
								?.cleanup()
								.catch(error => console.error('schmancy-zoom-stage: could not release the document', error))
						}),
					)
				}),
			),
			// The host's own box, not the window's: an overlay settles its width
			// after mount and a phone's URL bar changes the height mid-read, and
			// neither is a window resize. The first delivery is the initial measure.
			fromResizeObserver(this).pipe(tap(() => this.measure())),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		// The subscriptions leave with `disconnecting`; the parsed document does
		// not — its pages and fonts sit on the worker until released, and an
		// overlay that closes is the commonest way this element ends.
		//
		// KNOWN GAP, not closed here: this releases the document and nothing ever
		// reads it back. `requested$` is pushed from `willUpdate` on a FILE change
		// alone, so an element that is disconnected and reconnected — a reparent,
		// which this base class supports and tests — comes back with `this.doc`
		// undefined and never asks for another read, leaving the stage permanently
		// blank. Closing it wants a read re-requested on reconnect when a file is
		// set and no document is held, which is a lifecycle change worth its own
		// pass and its own reparent test.
		void this.doc?.cleanup().catch(() => {})
		this.doc = undefined
	}

	protected override willUpdate(changed: PropertyValues<this>): void {
		if (!changed.has('file') || !this.file) return
		const previous = changed.get('file')
		// A caller may rebuild its item on every render; only new content resets reading.
		if (previous?.previewUrl === this.file.previewUrl && previous?.mime === this.file.mime) return
		this.requested$.next()
	}

	// ── the content answers ────────────────────────────────────────────────────

	/**
	 * An image is one rectangle with no type to size against, so it keeps the
	 * photo convention — and a ceiling its own pixels pay for. `decode()` is the
	 * probe rather than a `load` listener because it resolves only once the
	 * bitmap is actually decodable, which is when `naturalWidth` is true.
	 */
	private readImage(): Observable<unknown> {
		const probe = new Image()
		probe.src = this.file.previewUrl
		return from(probe.decode()).pipe(
			tap(() => {
				this.naturalWidth = probe.naturalWidth
				this.pages = [{ index: 1, ratio: probe.naturalHeight / Math.max(1, probe.naturalWidth) }]
			}),
		)
	}

	/**
	 * A document answers with its own page count, its own page box, and — the one
	 * the reading scale turns on — its own body type, read from the text stream,
	 * whose runs carry their height in PDF points. So that scale is a measurement
	 * of THIS page rather than a constant that happens to suit A4 at one width.
	 */
	private readDocument(): Observable<unknown> {
		const url = this.file.previewUrl
		return from(import('pdfjs-dist')).pipe(
			switchMap(pdfjs => {
				this.pdfjs = pdfjs
				worker ??= pdfjs.PDFWorker.create({
					port: new Worker(new URL('../media-frame/pdf.worker.ts', import.meta.url), { type: 'module' }),
				})
				return from(pdfjs.getDocument({ url, worker }).promise)
			}),
			switchMap(doc => {
				this.doc = doc
				return from(doc.getPage(1)).pipe(
					switchMap(async first => {
						const heights: number[] = []
						if (doc.isPureXfa) {
							const runs = await first.getTextContent()
							for (const item of runs.items) {
								if ('height' in item && item.str.trim() !== '' && item.height > 0) heights.push(item.height)
							}
						} else {
							const reader = first.streamTextContent().getReader()
							try {
								while (true) {
									const { done, value } = await reader.read()
									if (done) break
									for (const item of value.items) {
										if ('height' in item && item.str.trim() !== '' && item.height > 0) heights.push(item.height)
									}
								}
							} finally {
								reader.releaseLock()
							}
						}
						return { doc, box: first.getViewport({ scale: 1 }), heights }
					}),
				)
			}),
			tap(({ doc, box, heights }) => {
				// The median run height is this document's own body type, in PDF
				// points.
				heights.sort((a, b) => a - b)
				this.bodyPt = heights[Math.floor(heights.length / 2)] ?? 0
				this.pageWidthPt = box.width
				this.pages = Array.from({ length: doc.numPages }, (_unused, index) => ({
					index: index + 1,
					ratio: box.height / box.width,
				}))
			}),
		)
	}

	// ── geometry ───────────────────────────────────────────────────────────────

	/** Re-reads the host box, re-derives this mime's own scale-1 box and the two
	 *  numbers that hang off it, re-clamps the reader's own position into the new
	 *  bounds, and re-renders. */
	private measure(): void {
		// Both axes, because an image's scale-1 box is derived from both. A zero
		// height would put `cw` at 0 and turn the ceiling into a pixel count.
		if (this.pages.length === 0 || this.clientWidth === 0 || this.clientHeight === 0) return
		const isPdf = this.file?.mime === 'application/pdf'
		const ratio = this.pages[0]?.ratio ?? 1
		this.cw = isPdf || ratio <= 0 ? this.clientWidth : Math.min(this.clientWidth, this.clientHeight / ratio)
		this.ch = isPdf
			? this.pages.reduce((total, page) => total + this.cw * page.ratio, 0) + PAGE_GAP_PX * (this.pages.length - 1)
			: this.cw * ratio
		// One image pixel to one device pixel where the file has that many, the
		// shared ceiling where it does not. The denominator is the box scale 1
		// lays the image out at — `cw`, not the host's width, which for a portrait
		// image on a landscape stage are different numbers.
		this.ceiling = isPdf ? CEILING : Math.max(PHOTO_CEILING, this.naturalWidth / Math.max(1, this.cw))
		const bodyAtFit = (this.bodyPt * this.cw) / Math.max(1, this.pageWidthPt)
		this.reading = !isPdf
			? PHOTO_READING
			: bodyAtFit > 0
				? clamp(BODY_MD_PX / bodyAtFit, 1.5, CEILING)
				: READING_FALLBACK
		// The column is laid out AT the scale-1 box rather than at `w-full`, which
		// is the whole of what makes an image's floor fit-both-axes.
		const column = this.columnRef.value
		if (column) column.style.width = `${this.cw}px`
		this.clampPan()
		this.paint()
		this.limits()
		this.rasterRequest$.next()
	}

	/**
	 * Content narrower than the host on an axis is centred and immovable on it;
	 * wider, and it travels until its own edge meets the host's and no further.
	 * A document that can be shoved into a corner has to be fetched back, and
	 * reading is not fetching.
	 */
	private clampPan(): void {
		const overX = this.cw * this.scale - this.clientWidth
		const overY = this.ch * this.scale - this.clientHeight
		this.tx = overX > 0 ? clamp(this.tx, -overX, 0) : -overX / 2
		this.ty = overY > 0 ? clamp(this.ty, -overY, 0) : -overY / 2
	}

	/** The same bounds, but a press past them gives rather than stops. */
	private clampPanElastic(): void {
		const overX = this.cw * this.scale - this.clientWidth
		const overY = this.ch * this.scale - this.clientHeight
		const axis = (value: number, over: number, span: number) => {
			if (over <= 0) return -over / 2 + resist(value + over / 2, span)
			if (value > 0) return resist(value, span)
			if (value < -over) return -over + resist(value + over, span)
			return value
		}
		this.tx = axis(this.tx, overX, this.clientWidth * OVERSCROLL)
		this.ty = axis(this.ty, overY, this.clientHeight * OVERSCROLL)
	}

	private paint(duration = 0): void {
		const column = this.columnRef.value
		if (!column) return
		// What the column will ACTUALLY travel, which under a reduced-motion
		// preference is nothing however long the caller asked for. The release
		// waits this out, so it has to be the travel that happened rather than the
		// travel that was asked for.
		const travel = duration > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? duration : 0
		column.style.transition = travel > 0 ? `transform ${travel}ms cubic-bezier(0.05,0.7,0.1,1)` : 'none'
		column.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
		// Which page covers the middle of the host — the one question a reader
		// zoomed into the middle of page 3 cannot answer from what is on screen.
		if (this.pages.length > 1) {
			const middle = (-this.ty + this.clientHeight / 2) / this.scale
			let top = 0
			for (const page of this.pages) {
				if (middle < top + this.cw * page.ratio + PAGE_GAP_PX / 2) {
					if (this.page !== page.index) {
						this.page = page.index
						if (this.pageRef.value) {
							this.pageRef.value.textContent = `${page.index} / ${this.pages.length}`
						}
						this.say(componentText('zoomStage.page', { page: page.index, total: this.pages.length }))
					}
					break
				}
				top += this.cw * page.ratio + PAGE_GAP_PX
			}
		}
		this.moved$.next(travel)
	}

	private showReadout(visible: boolean): void {
		const readout = this.readoutRef.value
		if (readout) readout.style.opacity = visible ? '1' : '0'
	}

	/** The only writer of the live region, so a page and a scale cannot each
	 *  overwrite the other's sentence unnoticed. */
	private say(sentence: string): void {
		if (this.liveRef.value) this.liveRef.value.textContent = sentence
	}

	// ── zoom ───────────────────────────────────────────────────────────────────

	/**
	 * Moves to `next`, keeping the content point under (px, py) under (px, py).
	 * Every zoom goes through here, so pinch, double-tap, ctrl-wheel and the
	 * keyboard cannot disagree about what an anchor is.
	 */
	private zoomTo(next: number, px: number, py: number, duration: number, elastic = false): void {
		const box = this.getBoundingClientRect()
		const target = clamp(next, elastic ? FIT * ELASTIC_LOW : FIT, elastic ? this.ceiling * ELASTIC_HIGH : this.ceiling)
		const anchorX = (px - box.left - this.tx) / this.scale
		const anchorY = (py - box.top - this.ty) / this.scale
		this.scale = target
		this.tx = px - box.left - anchorX * target
		this.ty = py - box.top - anchorY * target
		if (elastic) this.clampPanElastic()
		else this.clampPan()
		this.paint(duration)
	}

	/** Where a keyboard or button zoom anchors: the middle of what is being
	 *  looked at, because a key press has no pointer. */
	private centre(): { x: number; y: number } {
		const box = this.getBoundingClientRect()
		return { x: box.left + box.width / 2, y: box.top + box.height / 2 }
	}

	/**
	 * One discrete zoom, and the ONLY way one happens — so the `+` key, the
	 * zoom-in button and the double tap cannot disagree about the anchor, the
	 * raster or the sentence that is said.
	 */
	private step(next: number, at: { x: number; y: number }, duration: number): void {
		// No raster here. The paint above starts a transition, and the release
		// that waits it out is the only thing that asks for pixels — a raster
		// kicked off now is drawn for a scale the reader is already past, and its
		// decode lands in the middle of the frames the transition owes.
		this.zoomTo(next, at.x, at.y, duration)
		this.limits()
		this.announcedScale = this.scale
		this.say(componentText('zoomStage.zoomed', { percent: Math.round(this.scale * 100) }))
	}

	/** At fit there is no zooming out left, at the ceiling no zooming in — which
	 *  is the whole of what this surface says about scale, because the pair's own
	 *  state already answers the only question a reader asks of it. */
	private limits(): void {
		if (this.zoomOutRef.value) this.zoomOutRef.value.disabled = this.scale <= FIT + 0.01
		if (this.zoomInRef.value) this.zoomInRef.value.disabled = this.scale >= this.ceiling - 0.01
	}

	/** The release: clamp hard, re-rasterize, announce. */
	private settle(): void {
		const before = { tx: this.tx, ty: this.ty, scale: this.scale }
		this.scale = clamp(this.scale, FIT, this.ceiling)
		this.clampPan()
		const travelled = Math.max(Math.abs(before.tx - this.tx), Math.abs(before.ty - this.ty))
		const sprung = travelled > 0 || before.scale !== this.scale
		// A settled pan can reveal an undrawn page at the same scale. Reusing
		// same-size rasters below avoids repainting pages that already answered.
		// No paint when nothing moved: paint emits moved$, whose idle callback is
		// this method, so doing so would keep the stage settling forever.
		if (sprung) this.paint(Math.min(SNAP_MS, Math.max(ZOOM_MS, travelled * BOUNCE_MS_PER_PX)))
		this.rasterRequest$.next()
		this.limits()
		// Only when the scale is what moved. A pan settles here too, and its page
		// has already been said.
		if (Math.abs(this.announcedScale - this.scale) > 0.01) {
			this.announcedScale = this.scale
			this.say(componentText('zoomStage.zoomed', { percent: Math.round(this.scale * 100) }))
		}
		this.settle$.next()
	}

	// ── drawing ────────────────────────────────────────────────────────────────

	/** What each page inside the band should be carrying right now, at the
	 *  geometry as it stands. A page outside the band asks for nothing: the
	 *  pixels it already holds keep it readable the moment it comes back. */
	private rasters(): Raster[] {
		const doc = this.doc
		if (!doc || this.cw === 0) return []
		const dpr = window.devicePixelRatio || 1
		const budget = Math.min(
			window.matchMedia('(pointer: coarse)').matches ? MAX_CANVAS_PX_TOUCH : MAX_CANVAS_PX_POINTER,
			MAX_CANVAS_AREA,
		)
		const laidOutAt = Math.round(this.cw)
		const wanted: Raster[] = []
		for (const page of this.pages) {
			const canvas = this.renderRoot.querySelector<HTMLCanvasElement>(`canvas[data-page="${page.index}"]`)
			if (!canvas) continue
			if (!this.onScreen.has(page.index)) continue
			// Both axes scale with k, so a pixel budget is a bound on k itself. Floor
			// the pixels rather than ceil them: a rounded-up edge is what would push
			// a page that just fits back over the refusal.
			const height = this.cw * page.ratio
			const k = Math.min(this.scale * dpr, Math.sqrt(budget / (this.cw * height)))
			wanted.push({ doc, page: page.index, width: Math.floor(this.cw * k), laidOutAt })
		}
		return wanted
	}

	/**
	 * Per page, the latest request supersedes that page's own in-flight work and
	 * leaves its siblings' alone, and a page already carrying what the request
	 * wants is asked for nothing.
	 *
	 * The canvas and the text layer answer to DIFFERENT keys, so each leg takes
	 * this pipe with its own `same`. MEASURED on the running app, one shared key
	 * over the pair is wrong: `width` is
	 * `floor(cw * min(scale * dpr, sqrt(budget / (cw² * ratio))))`, and on the
	 * clamped branch — which the budget note above says is reached in ordinary
	 * reading near the ceiling — `cw` cancels, leaving the width constant
	 * whatever the host box. At the ceiling a 1024px → 968px resize therefore
	 * asked for the identical raster and was dropped, taking the layer's own
	 * relayout with it: the layer stayed 5120 CSS px wide over 4840px of ink,
	 * its spans still sized for the box before the resize, so selection and the
	 * accessibility tree named glyphs the reader was not looking at.
	 *
	 * `attempt` is how a page that failed gets another chance, and `DRAW_ATTEMPTS`
	 * is why it is not an unbounded one. A failure is recorded after the request
	 * was already compared, so without the count the next identical request is
	 * dropped and the page keeps whatever stale pixels it had with no path back —
	 * and since one page failing no longer fails the stage, nothing says so
	 * either. Without the ceiling, the opposite: a page that always fails is
	 * re-decoded on the shared worker on every gesture, queued ahead of the pages
	 * that CAN draw, for the life of the mount.
	 *
	 * Cancellation never reaches here. Each leg swallows its OWN exception inside
	 * `work` — `RenderingCancelledException` for a raster, `AbortException` for a
	 * text layer — because this one `catchError` cannot tell which leg it is
	 * serving, and a name it did not know would log a failure that never happened
	 * and spend an attempt on it.
	 */
	private perPage(
		same: (before: Raster, now: Raster) => boolean,
		work: (request: Raster) => Observable<unknown>,
	): OperatorFunction<Raster, unknown> {
		const attempts = new Map<number, number>()
		return source =>
			source.pipe(
				groupBy(request => request.page),
				mergeMap(page =>
					page.pipe(
						filter(request => (attempts.get(request.page) ?? 0) < DRAW_ATTEMPTS),
						map(request => ({ request, attempt: attempts.get(request.page) ?? 0 })),
						distinctUntilChanged((before, now) => before.attempt === now.attempt && same(before.request, now.request)),
						switchMap(({ request }) =>
							work(request).pipe(
								catchError(error => {
									const spent = (attempts.get(request.page) ?? 0) + 1
									attempts.set(request.page, spent)
									console.error(
										spent < DRAW_ATTEMPTS
											? `schmancy-zoom-stage: could not draw page ${request.page}, will try again`
											: `schmancy-zoom-stage: could not draw page ${request.page}, giving up on it`,
										error,
									)
									return of(null)
								}),
							),
						),
					),
				),
			)
	}

	/**
	 * One page's pixels, at the scale the reader chose, so they are looking at
	 * type rather than at a magnified picture of type.
	 *
	 * The raster runs only on settle: a pinch rides the CSS transform, and the
	 * sharp pixels land within a frame of letting go.
	 *
	 * An image has no document and never reaches here — its own pixels are the
	 * raster, and the browser resamples them.
	 */
	private drawPage(request: Raster): Observable<unknown> {
		// `defer`, so a synchronous refusal — a canvas the template dropped
		// between the request and the draw, a viewport pdf.js throws on — reaches
		// this stream, where the per-page catchError above can isolate the page.
		// Thrown while building the Observable it would escape onto the outer
		// chain instead, which this component's one long-lived subscription
		// deliberately leaves unguarded.
		return defer(() => {
			const canvas = this.renderRoot.querySelector<HTMLCanvasElement>(`canvas[data-page="${request.page}"]`)
			if (!canvas) return EMPTY
			return from(request.doc.getPage(request.page)).pipe(
				switchMap(loaded => {
					const natural = loaded.getViewport({ scale: 1 })
					const viewport = loaded.getViewport({ scale: request.width / natural.width })
					const height = Math.round(viewport.height)
					// THE SHARPER RASTER IS DRAWN OFF-SCREEN AND SWAPPED IN WHOLE.
					// Assigning to canvas.width empties the backing store, and the new
					// pixels only arrive when pdf.js resolves — tens to hundreds of ms
					// later. Drawn straight into the visible canvas, that window is a
					// blank page over this element's pinned PAPER: ink gone, white
					// sheet, ink back, once per distinct raster width. MEASURED on the
					// running app: one ink-free frame per zoom press, bounded at 26ms
					// at a 2496px raster and 38ms at 3444px, and growing with it.
					//
					// The buffer costs one more canvas of the SAME size the budget
					// above already bounds, so no single canvas crosses
					// MAX_CANVAS_AREA, and the one `finalize` that owns it releases it
					// the moment the blit is done — never held per page beyond its own
					// draw.
					const buffer = document.createElement('canvas')
					buffer.width = request.width
					buffer.height = height
					const task = loaded.render({ canvas: buffer, viewport, background: PAPER })
					return from(task.promise).pipe(
						tap(() => {
							const target = canvas.getContext('2d')
							// Thrown, not returned: a refused context leaves the canvas at
							// its old backing store, which is the stale-pixels-and-silence
							// the `defer` above exists to route to the per-page catchError.
							if (!target) throw new Error(`the engine refused a 2d context for page ${request.page}`)
							// The resize that empties the backing store and the blit that
							// refills it are one synchronous step, so the browser has no
							// frame boundary between them to composite an empty page in.
							canvas.width = request.width
							canvas.height = height
							target.drawImage(buffer, 0, 0)
						}),
						finalize(() => {
							// Unsubscribing IS how a superseded raster is cancelled, and this
							// is the one place that knows the task and the buffer. Both calls
							// are no-ops once the draw has landed.
							task.cancel()
							buffer.width = 0
							buffer.height = 0
						}),
						// This leg's own cancellation, swallowed before the shared
						// `catchError` can read it as a page that would not draw.
						catchError(error =>
							error instanceof Error && error.name === 'RenderingCancelledException' ? EMPTY : throwError(() => error),
						),
					)
				}),
			)
		})
	}

	/**
	 * pdf.js's own text layer over one page, so the document's text stays
	 * selectable, findable and present in the accessibility tree — which a
	 * canvas alone destroys, and which the `<embed>` this replaces gives for
	 * free on desktop.
	 *
	 * It is laid out once per SCALE-1 box and not per raster: percentages carry
	 * the spans' positions and `--total-scale-factor` carries their size, so the
	 * column's own transform magnifies the layer with the pixels underneath and
	 * a zoom moves it without rebuilding it. Which is why it keys on
	 * `laidOutAt` and the raster keys on `width` — see `perPage`.
	 *
	 * `getPage` here is the same proxy the raster leg asks for; pdf.js caches it
	 * per document, so the second ask is a resolved lookup and not a second
	 * parse.
	 */
	private layOutText(request: Raster): Observable<unknown> {
		return defer(() => {
			const layer = this.renderRoot.querySelector<HTMLElement>(`[data-text-layer="${request.page}"]`)
			const pdfjs = this.pdfjs
			if (!layer || !pdfjs) return EMPTY
			return from(request.doc.getPage(request.page)).pipe(
				switchMap(loaded => {
					const scale = request.laidOutAt / loaded.getViewport({ scale: 1 }).width
					this.textLayers.get(layer)?.cancel()
					this.textLayers.delete(layer)
					// THE LAYER IS BUILT DETACHED AND SWAPPED IN WHOLE, for the reason
					// the raster is. Emptying the live layer here and letting pdf.js
					// refill it over the following frames — against the same single
					// worker the page decode is queued on — leaves the page with no
					// selectable text, no find-in-page target and nothing in the
					// accessibility tree for that whole window, over ink that is
					// already correct.
					//
					// pdf.js sizes the container as
					// `round(down, var(--total-scale-factor) * <pageWidth>px, …)`
					// (`pdf.mjs:1517`), so the var has to be on THIS container for its
					// own width to resolve, and travels to the live one with the rest
					// of the inline style at the swap.
					const built = document.createElement('div')
					built.style.setProperty('--total-scale-factor', String(scale))
					return from(
						request.doc.isPureXfa ? loaded.getTextContent() : Promise.resolve(loaded.streamTextContent()),
					).pipe(
						switchMap(textContentSource => {
							const textLayer = new pdfjs.TextLayer({
								textContentSource,
								container: built,
								viewport: loaded.getViewport({ scale }),
							})
							this.textLayers.set(layer, textLayer)
							return from(textLayer.render()).pipe(
								tap(() => {
									// One swap. `laidOutAt` is written HERE and not at the
									// start, so a layout cut short by the next request leaves
									// no tag claiming a box it never reached.
									layer.style.cssText = built.style.cssText
									const rotation = built.getAttribute('data-main-rotation')
									if (rotation !== null) layer.setAttribute('data-main-rotation', rotation)
									layer.replaceChildren(...built.children)
									layer.dataset.laidOutAt = String(request.laidOutAt)
								}),
								finalize(() => {
									textLayer.cancel()
									if (this.textLayers.get(layer) === textLayer) this.textLayers.delete(layer)
								}),
							)
						}),
						// This leg's own cancellation. The read's reset cancels every live
						// text layer while this chain is still subscribed, so without this
						// the shared `catchError` would read a deliberate abort as a page
						// that would not draw — logging a failure that never happened and
						// spending one of its attempts.
						catchError(error =>
							error instanceof Error && error.name === 'AbortException' ? EMPTY : throwError(() => error),
						),
						catchError(error => {
							// The console cannot be the only answer: a reader who cannot see
							// the ink is exactly the one this layer exists for, and the page
							// is about to have no text with nothing said about it.
							this.say(componentText('zoomStage.pageTextFailed', { page: request.page }))
							return throwError(() => error)
						}),
					)
				}),
			)
		})
	}

	// ── gestures ───────────────────────────────────────────────────────────────

	private onPointerDown = (event: PointerEvent): void => {
		// Native text selection owns mouse presses on PDF ink, including the
		// double-click that selects a word. Capturing or cancelling that press
		// suppresses selection before the browser can start it.
		if (
			event.pointerType === 'mouse' &&
			event.button === 0 &&
			event.target instanceof Element &&
			event.target.matches('[data-text-layer] :is(span, br)')
		) {
			this.settle$.next()
			this.lastTap = undefined
			event.stopPropagation()
			return
		}
		// Claimed only where the press is actually taken: an overlay reads
		// `defaultPrevented` to stand its swipe-to-dismiss down, so content that
		// fits its host with nothing to pan would trade a dismissal for nothing.
		// A second finger is always a pinch and always claimed.
		if (
			this.pointers.size >= 1 ||
			this.cw * this.scale > this.clientWidth + 1 ||
			this.ch * this.scale > this.clientHeight + 1
		) {
			event.preventDefault()
		}
		// Captured on the stage rather than the host, because a captured pointer's
		// later events are dispatched AT the capture element: capturing on the host
		// would route every pointermove past the stage's own listener, which is a
		// descendant and never sees an event targeted above it.
		this.stageRef.value?.setPointerCapture(event.pointerId)
		this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
		const column = this.columnRef.value
		if (column) column.style.transition = 'none'

		if (this.pointers.size === 1) {
			this.pressed = {
				x: event.clientX,
				y: event.clientY,
				tx: this.tx,
				ty: this.ty,
				at: event.timeStamp,
			}
			this.samples = [{ t: event.timeStamp, x: event.clientX, y: event.clientY }]
			return
		}
		if (this.pointers.size === 2) {
			const [first, second] = [...this.pointers.values()]
			if (!first || !second) return
			const box = this.getBoundingClientRect()
			const midX = (first.x + second.x) / 2
			const midY = (first.y + second.y) / 2
			this.pinch = {
				distance: Math.max(1, Math.hypot(second.x - first.x, second.y - first.y)),
				scale: this.scale,
				ax: (midX - box.left - this.tx) / this.scale,
				ay: (midY - box.top - this.ty) / this.scale,
			}
			this.pressed = undefined
		}
	}

	private onPointerMove = (event: PointerEvent): void => {
		if (!this.pointers.has(event.pointerId)) return
		this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

		if (this.pinch && this.pointers.size >= 2) {
			const [first, second] = [...this.pointers.values()]
			if (!first || !second) return
			const box = this.getBoundingClientRect()
			const midX = (first.x + second.x) / 2
			const midY = (first.y + second.y) / 2
			const raw = (this.pinch.scale * Math.hypot(second.x - first.x, second.y - first.y)) / this.pinch.distance
			// Past either end the pinch gives instead of stopping — the difference
			// between a wall and a limit.
			this.scale =
				raw < FIT
					? FIT - resist(FIT - raw, FIT * (1 - ELASTIC_LOW))
					: raw > this.ceiling
						? this.ceiling + resist(raw - this.ceiling, this.ceiling * (ELASTIC_HIGH - 1))
						: raw
			this.tx = midX - box.left - this.pinch.ax * this.scale
			this.ty = midY - box.top - this.pinch.ay * this.scale
			this.clampPanElastic()
			this.paint()
			return
		}

		if (!this.pressed) return
		this.tx = this.pressed.tx + event.clientX - this.pressed.x
		this.ty = this.pressed.ty + event.clientY - this.pressed.y
		this.clampPanElastic()
		this.paint()
		this.samples.push({ t: event.timeStamp, x: event.clientX, y: event.clientY })
		if (this.samples.length > 6) this.samples.shift()
	}

	private onPointerUp = (event: PointerEvent): void => {
		if (!this.pointers.has(event.pointerId)) return
		const wasAlone = this.pointers.size === 1
		const press = this.pressed
		this.pointers.delete(event.pointerId)
		this.stageRef.value?.releasePointerCapture(event.pointerId)

		if (this.pointers.size < 2) this.pinch = undefined
		if (this.pointers.size === 1) {
			// One finger left after a pinch: it becomes the pan, from where it is.
			const [remaining] = [...this.pointers.values()]
			if (!remaining) return
			this.pressed = {
				x: remaining.x,
				y: remaining.y,
				tx: this.tx,
				ty: this.ty,
				at: event.timeStamp,
			}
			this.samples = [{ t: event.timeStamp, x: remaining.x, y: remaining.y }]
			return
		}

		if (wasAlone && press) {
			const travelled = Math.hypot(event.clientX - press.x, event.clientY - press.y)
			if (travelled < TAP_MOVE && event.timeStamp - press.at < TAP_MS) {
				this.pressed = undefined
				const now = performance.now()
				const isSecond =
					!!this.lastTap &&
					now - this.lastTap.at < DOUBLE_TAP_MS &&
					Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < DOUBLE_TAP_PX
				if (!isSecond) {
					this.lastTap = { at: now, x: event.clientX, y: event.clientY }
					return
				}
				this.lastTap = undefined
				// To this file's own reading scale, anchored where the finger landed;
				// and from anywhere above fit, straight back to fit. Back-out is
				// unconditional, so a reader never has to remember which scale they
				// are at to know what the second tap will do.
				this.step(
					this.scale > FIT + 0.01 ? FIT : this.reading,
					{ x: event.clientX, y: event.clientY },
					DOUBLE_TAP_ZOOM_MS,
				)
				return
			}
			const first = this.samples[0]
			const last = this.samples[this.samples.length - 1]
			const span = last && first ? last.t - first.t : 0
			// An overscroll springs back rather than flings, and a window too thin to
			// measure carries no velocity.
			if (span > 8 && first && last && this.scale >= FIT && this.scale <= this.ceiling) {
				this.fling$.next({ vx: (last.x - first.x) / span, vy: (last.y - first.y) / span })
			}
		}
		this.pressed = undefined
		this.settle()
	}

	private onPointerCancel = (event: PointerEvent): void => {
		if (!this.pointers.has(event.pointerId)) return
		this.pointers.delete(event.pointerId)
		this.pinch = undefined
		this.pressed = undefined
		this.settle()
	}

	override render() {
		const isPdf = this.file?.mime === 'application/pdf'
		return html`
			<div
				${ref(this.stageRef)}
				data-stage
				class="absolute inset-0 bg-scrim"
				tabindex="0"
				role="group"
				aria-roledescription=${isPdf ? componentText('zoomStage.document') : componentText('zoomStage.image')}
				aria-label=${this.file?.name || componentText('zoomStage.file')}
				aria-describedby="zoom-keys"
				@pointerdown=${this.onPointerDown}
				@pointermove=${this.onPointerMove}
				@pointerup=${this.onPointerUp}
				@pointercancel=${this.onPointerCancel}
			>
				${when(
					this.phase === 'reading',
					() => html`
						<div class="absolute inset-0 grid place-items-center">
							<schmancy-progress indeterminate class="w-32"></schmancy-progress>
						</div>
					`,
				)}
				${when(
					this.phase === 'failed',
					() => html`
						<div class="absolute inset-0 z-20 grid place-items-center bg-surface-containerLowest px-6">
							<schmancy-typography type="body" token="md" role="alert">
								${componentText('zoomStage.failed')}
							</schmancy-typography>
						</div>
					`,
				)}
				<!-- The gap belongs to the column, never to each page: a per-page
				     bottom margin adds one more gap after the LAST page and the clamp
				     counts n-1 of them. It rides style= with the transform-origin the
				     geometry above measures against, so neither can drift from the
				     constants that read them. The width is written by measure(),
				     because it is the scale-1 box and that is per mime. -->
				<div
					${ref(this.columnRef)}
					class="absolute left-0 top-0 grid"
					style="transform-origin:0 0;will-change:transform;gap:${PAGE_GAP_PX}px"
				>
					${when(
						isPdf,
						() => html`
							${repeat(
								this.pages,
								page => page.index,
								page => html`
									<div
										class="relative w-full"
										data-page-box=${page.index}
										role="region"
										aria-label=${componentText('zoomStage.page', { page: page.index, total: this.pages.length })}
										style="aspect-ratio:1/${page.ratio}"
									>
										<canvas
											data-page=${page.index}
											class="absolute inset-0 block h-full w-full"
											style="background:${PAPER}"
											aria-hidden="true"
										></canvas>
										<div data-text-layer=${page.index}></div>
									</div>
								`,
							)}
						`,
						() => html`
							<img
								class="block w-full select-none"
								src=${this.file?.previewUrl ?? ''}
								alt=${this.file?.name ?? ''}
								draggable="false"
							/>
						`,
					)}
				</div>
			</div>

			<!--
			    THE ZOOM PAIR. WCAG 2.5.1 (Level A) asks that anything operated by a
			    multipoint gesture also be operable with a single pointer and no path,
			    and a pinch is multipoint. Double-tap is single-pointer but it is a
			    TOGGLE — fit or the reading scale, nothing between — so without these
			    a person who cannot pinch reaches two scales where a pinching reader
			    reaches every one.

			    It renders wherever the gesture does, in an embedded pane as much as
			    an overlay. schmancy-file-viewer drops its top bar when embedded
			    because a HOST frames the pane and states identity and dismissal; that
			    reasoning reaches chrome the host duplicates, and reaches nothing the
			    content itself implements. A host cannot stand in for this pair,
			    because the gesture it substitutes for is this element's own.

			    It sits BESIDE the stage rather than inside it. MEASURED: inside, the
			    stage's own pointerdown handler captured the press and the button
			    never received a click — a control drawn over a surface that claims
			    every pointer is a control nobody can press.

			    z-30, because the viewer's own info scrim is an absolute inset-0 z-10
			    over this whole stage: a pair with no z-index paints beneath it and is
			    un-pressable while Info is open. Neither this host nor the viewer's
			    stage div opens a stacking context (both are positioned at z-index
			    auto), so this number competes with that scrim's directly.
			  -->
			${when(
				this.phase === 'ready',
				() => html`
					<schmancy-surface
						type="containerHigh"
						opacity="translucent"
						elevated
						class="absolute bottom-4 right-4 z-30 grid rounded-full"
					>
						<schmancy-icon-button
							${ref(this.zoomOutRef)}
							icon="remove"
							size="lg"
							aria-label=${componentText('zoomStage.zoomOut')}
							@click=${() => this.step(this.scale / STEP, this.centre(), ZOOM_MS)}
						></schmancy-icon-button>
						<schmancy-icon-button
							${ref(this.zoomInRef)}
							icon="add"
							size="lg"
							aria-label=${componentText('zoomStage.zoomIn')}
							@click=${() => this.step(this.scale * STEP, this.centre(), ZOOM_MS)}
						></schmancy-icon-button>
					</schmancy-surface>
				`,
			)}

			<!-- The one transient. It answers the single question the layout
			     genuinely hides — where in the document am I — and only where there
			     is more than one page to be lost in. -->
			${when(
				this.pages.length > 1,
				() => html`
					<schmancy-surface
						${ref(this.readoutRef)}
						type="containerHigh"
						opacity="translucent"
						elevated
						class="pointer-events-none absolute bottom-4 left-1/2 z-30 -translate-x-1/2 rounded-full px-3 py-1 transition-opacity duration-200"
						style="opacity:0"
					>
						<schmancy-typography ${ref(this.pageRef)} type="label" token="sm" class="tabular-nums">
							1 / ${this.pages.length}
						</schmancy-typography>
					</schmancy-surface>
				`,
			)}

			<span id="zoom-keys" class="sr-only">${componentText('zoomStage.keys')}</span>
			<span ${ref(this.liveRef)} class="sr-only" role="status" aria-live="polite"></span>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-zoom-stage': SchmancyZoomStage
	}
}
